'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import { use, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Nav, NavItem } from 'reactstrap';
import { MdClear, MdMenu } from 'react-icons/md';

import Avatar from 'shared/components/mySynergie/Avatar';
import { plausibleEvent } from 'shared/utils/plausibleEvent';
import LanguageDropdown from 'shared/components/nav/LanguageDropdown';
import MySynergieDropdown from 'shared/components/nav/MySynergieDropdown';
import { Dictionaries } from 'shared/i18n/i18n-config';
import { usePathname } from 'next/navigation';
import { getI18nUtils } from 'shared/i18n/i18n-utils';
import { defaultLocales } from '../../config';
import { menu, deviantLocales } from './menuConfig';
import splitLanguageFromPath from '../../utils/splitLanguageFromPath';
import { capitalize } from 'lodash';
import { useUser } from 'shared/contexts/UserContext';

// css: sandyou.be/stylesheets/layout/navbar.scss

export default function Navigation({ dictionary }: { dictionary: Partial<Dictionaries> }) {
  const { locale, pathname } = splitLanguageFromPath(usePathname());
  const { isLoggedIn } = useUser();

  const [menuIndex, setMenuIndex] = useState(-1);
  const [subMenu, setSubMenu] = useState(-1);
  const [menuElements, setMenuElements] = useState<HTMLElement[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { formatMessage } = getI18nUtils(dictionary);
  const availableLocales = deviantLocales[pathname] ?? defaultLocales.map((l) => ({ locale: l, pathname }));
  const topMenu = menu.filter((item) => item.topMenu !== -1).sort((a, b) => a.topMenu - b.topMenu);

  useEffect(() => {
    setMenuElements(
      topMenu
        .map((menuItem) => document.getElementById(`menu_item_${menuItem.name}`))
        .filter((el): el is HTMLElement => el !== null)
    );
  }, []);

  useEffect(() => {
    if (pathname === '/' || pathname.startsWith('/jobs') || pathname.startsWith('/blog')) {
      setMenuIndex(0);
      setSubMenu(0);
    }
    if (pathname.startsWith('/employer')) {
      setMenuIndex(1);
      setSubMenu(1);
    }
    if (pathname.startsWith('/about')) {
      setMenuIndex(2);
      setSubMenu(2);
    }
  }, [pathname]);

  useEffect(() => {
    if (mobileMenuOpen) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
  }, [mobileMenuOpen]);

  function addPlausibleEvent(e) {
    const path = e.target.pathname || e.target.href;
    if (path) plausibleEvent('navItemClick', { path });
  }

  return (
    <div
      className="bg-transparent position-absolute top-0 start-0 w-100 px-4 pb-5 px-lg-0 navbar navbar-expand-lg d-flex flex-column"
      onMouseLeave={() => setSubMenu(menuIndex)}
    >
      {/** TOP-MENU */}
      <div className="container-lg d-flex justify-content-between align-items-center p-0">
        {/* Logo */}
        <Link
          className="me-5"
          href={`/${locale}/`}
          locale={locale}
          onClick={() => {
            setMenuIndex(0);
            setSubMenu(0);
          }}
        >
          <Image src="/logos/logo_en_white.svg" alt="logo S&you" id="logo" width={100} height={80} priority />
        </Link>

        {/* Desktop navigation */}
        <Nav navbar className="flex-grow-1 show-desktop">
          {/* Buttons left */}
          <div className="flex-grow-1 d-flex gap-4 position-relative align-items-center h-auto">
            {topMenu.map((menuItem, index) => (
              <NavItem id={`menu_item_${menuItem.name}`} key={menuItem.name}>
                {menuItem.destination === pathname ? (
                  <p
                    tabIndex={0}
                    onFocus={() => setSubMenu(index)}
                    onMouseEnter={() => setSubMenu(index)}
                    className={`open-menu main-menu nav-link text-white m-0 p-0 h-auto ${index === subMenu && 'border-bottom border-1 border-white'}`}
                  >
                    {formatMessage({ id: menuItem.name })}
                  </p>
                ) : (
                  <Link
                    href={`/${locale}${menuItem.destination}`}
                    onFocus={() => setSubMenu(index)}
                    onMouseEnter={() => setSubMenu(index)}
                    className={`main-menu nav-link text-white p-0 h-auto ${index === subMenu && 'border-bottom border-1 border-white'}`}
                  >
                    {formatMessage({ id: menuItem.name })}
                  </Link>
                )}
              </NavItem>
            ))}
            {/* Moving border */}
            {menuIndex !== -1 && (
              <div
                className="bg-white position-absolute start-0"
                style={{
                  width: menuElements[menuIndex]?.offsetWidth,
                  height: '2px',
                  transform: `translateX(${menuElements[menuIndex]?.offsetLeft}px)`,
                  transition: 'width 0.5s ease-in-out, transform 0.5s ease-in-out',
                  bottom: '10px',
                }}
              />
            )}
          </div>
          {/* Buttons right */}
          <div className="buttons-right d-lg-flex align-items-center gap-3">
            <NavItem>
              <Link
                href={`/${locale}/contact/`}
                onClick={(e) => {
                  setMenuIndex(-1);
                  setSubMenu(-1);
                  addPlausibleEvent(e);
                }}
                className={`text-white ${pathname == '/contact' ? 'selected' : ''}`}
              >
                {capitalize(formatMessage({ id: 'contact' }))}
              </Link>
            </NavItem>
            <LanguageDropdown availableLocales={availableLocales} dictionary={dictionary} />
            <MySynergieDropdown dictionary={dictionary} />
          </div>
        </Nav>

        {/* Mobile navigation */}

        <div className="d-flex align-items-center gap-3 d-lg-none">
          <Link href={`/${locale}/my-synergie/`}>
            <Avatar dictionary={dictionary} />
          </Link>
          <div
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            onKeyDown={() => setMobileMenuOpen(!mobileMenuOpen)}
            role="button"
            tabIndex={0}
            style={{ zIndex: '999' }}
          >
            {mobileMenuOpen ? <MdClear className="text-black" size={24} /> : <MdMenu className="text-white" size={24} />}
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu fade-in ">
            <Link className="me-5" href={`/${locale}/`} locale={locale} onClick={() => setMobileMenuOpen(false)}>
              <Image src="/logos/logo_en_blue.svg" alt="logo S&you" id="logo" width={100} height={80} priority />
            </Link>
            <Nav className="d-flex flex-column">
              {topMenu.map((menuItem) => (
                <div key={menuItem.name}>
                  <NavItem className="mb-1">
                    <Link
                      onClick={() => setMobileMenuOpen(false)}
                      className="category"
                      href={`/${locale}${menuItem.destination}`}
                    >
                      {capitalize(formatMessage({ id: menuItem.name }))}
                    </Link>
                  </NavItem>
                  {menuItem.subMenu
                    .filter((a) => a.topMenu !== -1)
                    .map((subMenuItem) => (
                      <NavItem className="pt-3" key={subMenuItem.name}>
                        <Link
                          onClick={() => setMobileMenuOpen(false)}
                          className={`item text-base ${pathname.startsWith(subMenuItem.destination) ? 'selected' : ''}`}
                          href={`/${locale}${subMenuItem.destination}`}
                        >
                          {capitalize(formatMessage({ id: subMenuItem.name }))}
                        </Link>
                      </NavItem>
                    ))}
                  <hr className="mb-4" />
                </div>
              ))}
              {/* Contact and Language */}
              <NavItem>
                <Link
                  className={`item text-base ${pathname == '/contact' ? 'selected' : ''}`}
                  href={`/${locale}/contact/`}
                  onClick={(e) => {
                    addPlausibleEvent(e);
                    setMobileMenuOpen(false);
                  }}
                >
                  {capitalize(formatMessage({ id: 'contact' }))}
                </Link>
              </NavItem>
              <hr />
              <NavItem>
                <p className="category">{capitalize(formatMessage({ id: 'language' }))}</p>
                <div className="d-flex gap-3">
                  {defaultLocales.map((l) => (
                    <Link
                      key={l}
                      className={`item text-base ${locale == l ? 'selected' : ''}`}
                      href={`/${l}${pathname}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {l.toUpperCase()}
                    </Link>
                  ))}
                </div>
              </NavItem>
              <hr />
              {/* MySynergie */}
              {isLoggedIn ? (
                <div onClick={() => setMobileMenuOpen(false)}>
                  <MySynergieDropdown dictionary={dictionary} />
                </div>
              ) : (
                <Link onClick={() => setMobileMenuOpen(false)} className="text-black" href={`/${locale}/auth/login/`}>
                  Aanmelden
                </Link>
              )}
            </Nav>
          </div>
        )}
      </div>

      {/** SUB-MENU */}
      <div className="sub-menu container-lg p-0 pt-3 show-desktop">
        {subMenu !== -1 && (
          <Nav className="d-flex gap-4">
            {topMenu[subMenu].subMenu
              .filter((a) => a.topMenu !== -1)
              .map((sub) => (
                <NavItem key={sub.destination} className="fade-in-top" onClick={() => setMenuIndex(subMenu)}>
                  <Link
                    className={`item text-white  ${pathname.startsWith(sub.destination) ? 'selected' : ''}`}
                    href={`/${locale}${sub.destination}`}
                  >
                    {formatMessage({ id: sub.name })}
                  </Link>
                </NavItem>
              ))}
          </Nav>
        )}
      </div>
    </div>
  );
}
